import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Loader2, FileText, Truck, Receipt, Download, CreditCard, Wrench, Undo2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { downloadDoc } from "@/lib/downloadDoc";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function OrderDetail() {
  const { id } = useParams();
  const { t, fmtCurrency } = useI18n();
  const { can, modules } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: o, isLoading, refetch } = useQuery({ queryKey: ["order", id], queryFn: async () => (await api.get(`/orders/${id}`)).data });
  const { data: workflow } = useQuery({ queryKey: ["order-workflow", id], queryFn: async () => (await api.get(`/orders/${id}/workflow`)).data });

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const remaining = o.line_items.filter((l) => Number(l.quantity) - Number(l.delivered_qty || 0) > 0);
  const createDelivery = async () => {
    try {
      const lines = remaining.map((l) => ({ order_line_id: l.id, quantity: String(Number(l.quantity) - Number(l.delivered_qty || 0)) }));
      await api.post("/deliveries", { order_id: o.id, idempotency_key: `ui-${o.id}-${Date.now()}`, lines });
      toast.success(t("g2.action_done"));
      refetch();
      qc.invalidateQueries({ queryKey: ["order-workflow", id] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const invoiceable = (workflow?.lines || []).filter((line) => Number(line.invoiceable_qty) > 0);
  const createInvoice = async () => {
    try {
      const key = globalThis.crypto?.randomUUID?.() || `${Date.now()}`;
      const { data } = await api.post(`/orders/${o.id}/invoice`, {
        idempotency_key: key,
        lines: invoiceable.map((line) => ({ order_line_id: line.order_line_id, quantity: line.invoiceable_qty })),
      });
      toast.success(t("orders.invoice_created"));
      navigate(`/finance/einvoices/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const lineFlow = Object.fromEntries((workflow?.lines || []).map((line) => [line.order_line_id, line]));
  const createServiceJob = async () => {
    try {
      const { data } = await api.post(`/automotive/orders/${o.id}/service-job`, { vehicle_id: o.vehicle_id });
      toast.success(data.existing ? t("automotive.job_already_exists") : t("automotive.job_created"));
      navigate(`/jobs?open=${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const reverseToQuote = async () => {
    try {
      await api.post(`/quotes/${o.quote_id}/reverse-conversion`);
      toast.success(t("quotes.reverse_success"));
      navigate(`/quotes/${o.quote_id}`);
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };

  return (
    <div>
      <PageHeader
        title={o.order_number}
        subtitle={<span className="flex items-center gap-2">{o.customer_name} <StatusBadge status={o.status} labelKey={`orders.status_${o.status}`} /></span>}
        actions={<div className="flex gap-2">
          {modules?.includes("automotive_workshop") && can("jobs:write") && o.vehicle_id && <Button onClick={createServiceJob}><Wrench className="h-4 w-4" />{t("automotive.create_service_job")}</Button>}
          {can("deliveries:write") && remaining.length > 0 && <Button onClick={createDelivery} data-testid="create-delivery-button"><Truck className="h-4 w-4" />{t("g2.create_delivery") || "Lieferung"}</Button>}
          {can("einvoice:create") && workflow?.invoice_module_enabled && invoiceable.length > 0 && <Button onClick={createInvoice} data-testid="create-order-invoice-button"><Receipt className="h-4 w-4" />{t("orders.create_invoice")}</Button>}
          {o.quote_id && <Button variant="outline" onClick={() => navigate(`/quotes/${o.quote_id}`)} data-testid="order-view-quote"><FileText className="h-4 w-4" />{t("orders.from_quote")}</Button>}
          {o.quote_id && ["open", "reserved"].includes(o.status) && can("quotes:convert") && (
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="outline" data-testid="order-reverse-to-quote"><Undo2 className="h-4 w-4" />{t("orders.reverse_to_quote")}</Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>{t("quotes.reverse_title")}</AlertDialogTitle><AlertDialogDescription>{t("quotes.reverse_desc")}</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel><AlertDialogAction onClick={reverseToQuote}>{t("quotes.reverse_confirm")}</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button variant="outline" onClick={() => navigate("/orders")} data-testid="order-back"><ArrowLeft className="h-4 w-4" />{t("common.back")}</Button>
        </div>}
      />
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("quotes.description")}</TableHead>
              <TableHead className="text-right">{t("quotes.quantity")}</TableHead>
              <TableHead className="text-right">{t("orders.delivered")}</TableHead>
              <TableHead className="text-right">{t("orders.invoiced")}</TableHead>
              <TableHead className="text-right">{t("quotes.unit_price")}</TableHead>
              <TableHead className="text-right">{t("quotes.line_net")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {o.line_items.map((l) => (
              <TableRow key={l.id} data-testid={`order-line-${l.id}`}>
                <TableCell className="font-medium">{l.description}</TableCell>
                <TableCell className="text-right tabular-nums">{l.quantity} {l.unit}</TableCell>
                <TableCell className="text-right tabular-nums">{lineFlow[l.id]?.delivered_qty || "0"} {l.unit}</TableCell>
                <TableCell className="text-right tabular-nums">{lineFlow[l.id]?.invoiced_qty || "0"} {l.unit}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCurrency(l.unit_price)}</TableCell>
                <TableCell className="text-right tabular-nums font-medium">{fmtCurrency(l.line_net)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex justify-end border-t border-slate-200 p-4 dark:border-slate-800">
          <div className="w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">{t("quotes.subtotal")}</span><span className="tabular-nums">{fmtCurrency(o.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">{t("quotes.tax_total")}</span><span className="tabular-nums">{fmtCurrency(o.tax_total)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-1 font-heading text-base font-bold dark:border-slate-700"><span>{t("quotes.total")}</span><span className="tabular-nums" data-testid="order-total">{fmtCurrency(o.total)}</span></div>
          </div>
        </div>
      </Card>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="border-b border-slate-200 p-4 dark:border-slate-800"><h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("orders.deliveries")}</h3></div>
          {!workflow?.deliveries?.length ? <p className="p-4 text-sm text-slate-500">{t("orders.no_deliveries")}</p> : (
            <div className="divide-y divide-slate-200 dark:divide-slate-800">
              {workflow.deliveries.map((delivery) => (
                <div key={delivery.id} className="flex items-center justify-between p-4">
                  <div><div className="font-medium">{delivery.delivery_number}</div><div className="text-xs text-slate-500">{delivery.line_items?.length || 0} {t("orders.positions")}</div></div>
                  <Button variant="outline" size="sm" onClick={() => downloadDoc(`/deliveries/${delivery.id}/pdf`, `${delivery.delivery_number}.pdf`)}><Download className="h-4 w-4" />PDF</Button>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <div className="border-b border-slate-200 p-4 dark:border-slate-800"><h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("orders.invoices")}</h3></div>
          {!workflow?.invoices?.length ? <p className="p-4 text-sm text-slate-500">{t("orders.no_invoices")}</p> : (
            <div className="divide-y divide-slate-200 dark:divide-slate-800">
              {workflow.invoices.map((invoice) => (
                <button key={invoice.id} onClick={() => navigate(`/finance/einvoices/${invoice.id}`)} className="flex w-full items-center justify-between p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800">
                  <div><div className="font-medium">{invoice.number || t("einvoice.draft_no")}</div><div className="text-xs text-slate-500">{fmtCurrency(invoice.gross_total)} · {t(`einvoice.status.${invoice.status}`)}</div></div>
                  <div className="flex items-center gap-2 text-sm"><CreditCard className="h-4 w-4" /><span>{t(`orders.payment_${invoice.payment_status}`)}</span>{invoice.payment_status !== "paid" && <span className="tabular-nums text-slate-500">{fmtCurrency(invoice.open_amount)}</span>}</div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
